import { useMemo } from 'react';
import { Menu } from 'lucide-react';

import { SiteMenuMap } from '@/client/components/admin/Navigation/SiteMenuMap';
import { SecBarPanel } from '@/client/components/admin/Navigation/SecBarPanel';
import { MidSectionsPanel } from '@/client/components/admin/Navigation/MidSectionsPanel';
import { NavTreePanel } from '@/client/components/admin/Navigation/NavTreePanel';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useTabSearchParam } from '@/client/hooks/useTabSearchParam';

/** Tabs mirror public site layers (top → bottom). Legacy ids kept for deep links. */
const TAB_PARAM = ['header', 'page', 'footer', 'tree', 'mid', 'sec'] as const;
type TabParam = (typeof TAB_PARAM)[number];
type Surface = 'header' | 'page' | 'footer';

const LEGACY: Partial<Record<TabParam, Surface>> = {
  tree: 'footer',
  mid: 'page',
  sec: 'header',
};

const toSurface = (t: TabParam): Surface => LEGACY[t] ?? (t as Surface);

const NavigationPage = () => {
  useBreadcrumbs([
    { label: 'Navigation', href: '/admin/navigation' },
    { label: 'Menus' },
  ]);

  const [rawTab, setRawTab] = useTabSearchParam<TabParam>(TAB_PARAM, 'header');
  const active = useMemo(() => toSurface(rawTab), [rawTab]);
  const setActive = (surface: Surface) => setRawTab(surface);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div>
          <div className="mb-1 flex items-center gap-3">
            <Menu className="h-7 w-7 text-blue-700" />
            <h1 className="text-2xl font-bold text-gray-900">Navigation</h1>
          </div>
          <p className="ml-10 max-w-2xl text-sm text-gray-500">
            Edit the menus visitors see — header categories, on-page category strips, and footer
            links. Pick a layer below that matches the site.
          </p>
        </div>

        <SiteMenuMap active={active} onSelect={setActive} />

        {active === 'header' && <SecBarPanel />}
        {active === 'page' && <MidSectionsPanel />}
        {active === 'footer' && <NavTreePanel mode="footer" />}
      </div>
    </div>
  );
};

export default NavigationPage;
